import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../app/useAuth";
import { useUserProfilesByIds } from "./useUserProfiles";
import { fetchDashboardGroupBasicPolls } from "../lib/data/basicPolls";
import {
  deriveDashboardBasicPollItems,
  buildUsersFromIds,
} from "../features/dashboard/lib/dashboard-basic-polls";
import {
  canViewOtherVotesPublicly,
  getVoteIdentityDisplayMode,
} from "../lib/vote-visibility";
import {
  anonymizeUsers,
  buildAnonymousIdentityMap,
} from "../lib/anonymous-voter-identities";

/**
 * Fetches and enriches basic polls from shared groups for profile display.
 * Returns poll items ready for BasicPollCard rendering.
 */
export function useSharedBasicPolls({ sharedGroupIds = [], groupsById = {}, getGroupColor }) {
  const { user } = useAuth();
  const userId = user?.uid || null;

  const [sourceItems, setSourceItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const groupIdsKey = useMemo(
    () => sharedGroupIds.filter(Boolean).sort().join("|"),
    [sharedGroupIds]
  );

  useEffect(() => {
    let cancelled = false;
    if (!userId || !groupIdsKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
      setSourceItems([]);
      return;
    }
    const ids = groupIdsKey.split("|");
    setLoading(true);
    fetchDashboardGroupBasicPolls(ids, userId)
      .then((items) => {
        if (!cancelled) setSourceItems(items || []);
      })
      .catch((err) => {
        console.error("Failed to fetch shared basic polls:", err);
        if (!cancelled) setSourceItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupIdsKey, userId]);

  const groupNameById = useMemo(() => {
    const map = new Map();
    Object.values(groupsById).forEach((g) => {
      if (g?.id && g?.name) map.set(g.id, g.name);
    });
    return map;
  }, [groupsById]);

  const enrichedItems = useMemo(() => {
    if (!sourceItems.length) return [];
    return deriveDashboardBasicPollItems({
      basicPollSourceItems: sourceItems,
      groupsById,
      groupNameById,
      getGroupColor: getGroupColor || (() => null),
      userId,
      archivedPolls: [],
    });
  }, [sourceItems, groupsById, groupNameById, getGroupColor, userId]);

  // Collect user IDs for profile resolution
  const allUserIds = useMemo(() => {
    const ids = new Set();
    enrichedItems.forEach((poll) => {
      (poll.eligibleIds || []).forEach((id) => ids.add(id));
      (poll.voterIds || []).forEach((id) => ids.add(id));
    });
    return Array.from(ids);
  }, [enrichedItems]);

  const { profiles: profilesById } = useUserProfilesByIds(allUserIds);

  // Build final card-ready items with user objects. Apply the reveal-aware
  // identity display mode (shared viewer is a non-participant, so reveal is
  // resolved publicly) and anonymize/gate voter lists so a shared poll never
  // leaks real voter names under anonymization/hide.
  const cardItems = useMemo(() => {
    return enrichedItems.map((poll) => {
      const isCreator =
        String(poll?.creatorId || "").trim() === String(userId || "").trim();
      const detailsRevealed = canViewOtherVotesPublicly({
        voteVisibility: poll?.voteVisibility,
        allParticipantsVoted: poll?.votesAllSubmitted === true,
        isFinalized: (poll?.status || "OPEN") === "FINALIZED",
      });
      const voteIdentityDisplayMode = getVoteIdentityDisplayMode({
        isCreator,
        hideVoterIdentities: poll?.hideVoterIdentities,
        voteAnonymization: poll?.voteAnonymization,
        detailsRevealed,
      });
      const eligibleUsers = buildUsersFromIds(poll.eligibleIds, profilesById);
      const votedUsers = buildUsersFromIds(poll.voterIds, profilesById);
      const pendingUsers = buildUsersFromIds(poll.pendingIds, profilesById);

      if (voteIdentityDisplayMode === "anonymous") {
        const anonymousIdentityMap = buildAnonymousIdentityMap(
          [...eligibleUsers, ...votedUsers, ...pendingUsers],
          { scopeKey: `shared:${poll.parentType}:${poll.parentId}:${poll.pollId}` }
        );
        return {
          ...poll,
          eligibleUsers: anonymizeUsers(eligibleUsers, anonymousIdentityMap, {
            keyPrefix: "eligible",
            currentUser: user,
          }),
          votedUsers: anonymizeUsers(votedUsers, anonymousIdentityMap, {
            keyPrefix: "voted",
            currentUser: user,
          }),
          pendingUsers: anonymizeUsers(pendingUsers, anonymousIdentityMap, {
            keyPrefix: "pending",
            currentUser: user,
          }),
          showVoterIdentities: true,
          voteIdentityDisplayMode,
        };
      }

      return {
        ...poll,
        eligibleUsers,
        // Hidden mode: NEVER emit real voter/pending user objects. The summary
        // renders neutral placeholders from the COUNT fields, so stripping here
        // guarantees real identities can't leave the hook even if a downstream
        // component ignores showVoterIdentities.
        votedUsers: voteIdentityDisplayMode === "hidden" ? [] : votedUsers,
        pendingUsers: voteIdentityDisplayMode === "hidden" ? [] : pendingUsers,
        showVoterIdentities: voteIdentityDisplayMode !== "hidden",
        voteIdentityDisplayMode,
      };
    });
  }, [enrichedItems, profilesById, user, userId]);

  return { sharedBasicPolls: cardItems, loading };
}
